import React, { useEffect, useState, useContext } from 'react';
import { FilterContext } from '../FilterProvider';
import styled from 'styled-components';
import { AccordionGroup } from '../index';
import { cancerTypes } from '../../util/data/cancerTypes';
import { cancerTypesQlex } from '../../util/data/cancerTypesQlex';
import { indications } from '../../util/data/indications';
import { NotationBlock } from '../../util/typography';
import ComponentWrapper from '../layout/ComponentWrapper';
import IndicationCTA from './IndicationCTA';
import IndicationDosing from './IndicationDosing';
import { FeaturedCTA } from '../../components';
import { indicationDosingContent, cancerTypeFootnotes } from './dosingContent';
import { indicationQlexDosingContent, cancerTypeFootnotesQsub } from './qlexDosingContent';
import IndicationBrandTab from './IndicationBrandTab';

// @TODO: This component has grown in its scope and needs to be refactored to simplify the logic, break things out, and
// perhaps optimize performance.

const MaxWidthComponentWrapper = styled(ComponentWrapper)`
    max-width: 824px;
`;

export const sharedStrings = {
    efficacy: {
        listName: 'Efficacy',
        ctaDataPrefix: 'Efficacy',
        urlPrefix: '/efficacy/'
    },
    safety: {
        listName: 'Safety',
        ctaDataPrefix: 'Selected Adverse Reaction',
        urlPrefix: '/safety/adverse-reactions/'
    },
};


export const createKeynotesText = (keynotes) => {
    let prefixedKeynotes = keynotes.map(keynoteNumber => 'KEYNOTE&#8288;-&#8288;' + keynoteNumber);
    if (prefixedKeynotes.length === 1)
        return prefixedKeynotes[0];
    else {
        const lastKeynote = prefixedKeynotes.pop();
        const andSeparator = prefixedKeynotes.length === 1 ? ' and ' : ', and ';
        return [prefixedKeynotes.join(', '), lastKeynote].join(andSeparator);
    }
};

// For typical CTAs, use the keynotes/slug properties. For special cases, specify CTAs using efficacyCTA and safetyCTA.
export const triageCTAs = {
    1: { keynotes: ['868'], slug: 'advanced-or-recurrent-endometrial-carcinoma', QlexCTA:true },
    2: { keynotes: ['006'], slug: 'melanoma-monotherapy', QlexCTA:true},
    3: { keynotes: ['054', '716'], slug: 'melanoma-adjuvant-therapy', QlexCTA:true },
    4: { keynotes: ['189'], slug: 'nsclc-nonsquamous-first-line-combination-therapy', QlexCTA:true },
    5: { keynotes: ['407'], slug: 'nsclc-squamous-first-line-combination-therapy', QlexCTA:true },
    6: { keynotes: ['042'], efficacyKeynotes: ['024', '042'], slug: 'nsclc-first-line-monotherapy', QlexCTA:true },
    7: { keynotes: ['010'], slug: 'nsclc-second-line-monotherapy', QlexCTA:true },
    // 8: { keynotes: ['426'], slug: 'advanced-renal-cell-carcinoma' },
    9: { keynotes: ['048'], slug: 'hnscc-first-line-combination-therapy', QlexCTA:true },
    10: { keynotes: ['048'], slug: 'hnscc-first-line-monotherapy', QlexCTA:true },
    // For an internal link that opens in a new tab, use href instead of url and add target: '_blank'
    // Using url, CTA.setPropsFromURL will automatically set links starting with '/' to be an internal Gatsby Link
    //if we add these link exceptions, we need to add the indication numbers in sitemap.js in excludeIndictaions
    11: {
        efficacyCTA: {href: '/prescribing-information/', label: 'View Prescribing Information for KEYTRUDA', target: '_blank',  QlexPrescribing: true},
        safetyCTA: { href: '/prescribing-information/', label: 'View Prescribing Information for KEYTRUDA', target: '_blank', QlexCTA:true }
    },
    // 12: { },
    13: { keynotes: ['629'], slug: 'cutaneous-squamous-cell-carcinoma', QlexCTA:true },
    14: { keynotes: ['017', '913'], slug: 'merkel-cell-carcinoma', QlexCTA:true },
    15: { keynotes: ['052'], slug: 'urothelial-carcinoma-first-line-monotherapy', QlexCTA:true },
    16: { keynotes: ['045'], slug: 'urothelial-carcinoma-second-line-monotherapy', QlexCTA:true },
    // 17: { keynotes: ['057'], slug: 'nmibc', QlexCTA:true },
     17: {
        efficacyCTA: {href: '/prescribing-information/', label: 'View Prescribing Information for KEYTRUDA', target: '_blank',  QlexPrescribing: true},
        safetyCTA: { href: '/prescribing-information/', label: 'View Prescribing Information for KEYTRUDA', target: '_blank', QlexCTA:true }
    },
    18: { keynotes: ['859'], slug: 'gastric-adenocarcinoma', QlexCTA:true },
    19: { keynotes: ['590'], efficacyKeynotes: ['590', '180', '181'], slug: 'esophageal-carcinoma', QlexCTA:true },
    20: { keynotes: ['204'], efficacyKeynotes: ['204', '087'], slug: 'classical-hodgkin-lymphoma' },
    21: { keynotes: ['170'], slug: 'pmbcl' },
    22: { keynotes: ['158', '164', '051'], slug: 'msi-h-dmmr', QlexCTA:true },
    23: { keynotes: ['177'], slug: 'msi-h-dmmr-colorectal-cancer', QlexCTA:true },
    24: { keynotes: ['158'], slug: 'cervical-cancer-second-line-monotherapy', QlexCTA:true },
    // 25: { keynotes: ['158'], slug: 'tumor-mutational-burden-high' },
    26: { keynotes: ['355'], slug: 'triple-negative-breast-cancer', QlexCTA:true },
    27: { keynotes: ['522'], slug: 'early-stage-triple-negative-breast-cancer', QlexCTA:true },
    28: { keynotes: ['826'], slug: 'cervical-cancer-first-line-combination-therapy', QlexCTA:true },
    29: { keynotes: ['564'], slug: 'adjuvant-renal-cell-carcinoma', QlexCTA:true },
    // 30: { keynotes: ['158'], slug: 'advanced-endometrial-carcinoma' },
    31: {
        efficacyCTA: { url: 'https://www.welireghcp.com/adjuvant-renal-cell-carcinoma', label: 'Visit Site' },
        safetyCTA: { url: 'https://www.welireghcp.com/adjuvant-renal-cell-carcinoma', label: 'Visit Site' }
    },
    32: {
        efficacyCTA: { url: 'https://www.keytrudalenvimahcp.com/endometrial-carcinoma/', label: 'View Efficacy Data' },
        safetyCTA: { url: 'https://www.keytrudalenvimahcp.com/endometrial-carcinoma/', label: 'View Selected Adverse Reaction Data' }
    },
    33: { keynotes: ['091'], slug: 'nsclc-adjuvant-therapy', QlexCTA:true },
    34: { keynotes: ['671'], slug: 'nsclc-perioperative-regimen', QlexCTA:true },
    35: { keynotes: ['966'], slug: 'advanced-biliary-tract-cancer-combination-therapy', QlexCTA:true },
    36: { keynotes: ['A39'], slug: 'urothelial-cancer-first-line-combination-therapy', QlexCTA:true },
    37: { keynotes: ['A18'], slug: 'cervical-cancer-combination-chemoradiotherapy', QlexCTA:true },
    38: { keynotes: ['689'], slug: 'hnscc-squamous-cell-carcinoma', QlexCTA:true},
    39: { keynotes: ['811'], slug: 'her2-positive-gastric-adenocarcinoma-keynote-811', QlexCTA:true},
    40: { keynotes: ['905'], efficacyKeynotes: ['B15', '905'], safetyKeynotes: ['B15', '905'], slug: 'muscle-invasive-bladder-cancer-mibc', QlexCTA:true},
    41: { keynotes: ['B96'], slug: 'ovarian-cancer-keynote-b96', QlexCTA:true},
    42: { keynotes: ['D19'], slug: 'metastatic-triple-negative-breast-cancer', QlexCTA:true}, 
};

const TriageList = props => {
    let isBrowser = typeof window !== 'undefined' && window.location.protocol !== 'data:';
    let onHomepage = isBrowser && window.location.pathname == '/';

    const [hasMounted, setHasMounted] = useState(false);
    const { filteredCancerTypes } = useContext(FilterContext);

    //Ensure this component refreshes from JS and not Gatsby cache https://www.joshwcomeau.com/react/the-perils-of-rehydration/
    useEffect(() => {
        setHasMounted(true);
    }, []);
    if (!hasMounted) {
        return null;
    }

    //The canonical order of Cancer Types - removing items from this list will 'hide' them from all Triage list displays
    let cancerTypeOrder = [2,3,5,9,23,10,15,16,24,11,12,17,22,8,4,21,7,19, 25];
    let cancerTypeOrderQlex = [2,3,5,9,23,10,15,16,24,11,12,17,22,8,4,21,7,19, 25];

    let cancerTypeOrderList = props.brandType === 'qlex' 
                                ? cancerTypeOrderQlex
                                : cancerTypeOrder

    let cancerTypesList = props.brandType === 'qlex' 
                        ? cancerTypesQlex
                        : cancerTypes

    //filter the list, if applicable
    if (filteredCancerTypes.length) {
        cancerTypeOrderList = cancerTypeOrderList.filter((cancerTypeId) => filteredCancerTypes.includes(cancerTypeId))
    } else {
        // If there are no filters applied on the homepage, bypass the performance-hungry creation of cancerTypeList since we won't
        // render anyway. This can be made more elegant as part of a refactor.
        if (onHomepage) {
            return null;
        }
    }

 const cancerTypeList = cancerTypeOrderList
        .map((cancerTypeId, index) => {
            // const indicationsSource = props.brandType === 'qlex'
            //     ? cancerTypesQlex[cancerTypeId]?.indications
            //     : cancerTypes[cancerTypeId].indications;

            const indicationsSource = cancerTypesList[cancerTypeId].indications;
                
            const indicationBrandTabwithList = <IndicationBrandTab cancerTypeId={cancerTypeId} listType={props.listType} />

                                
            const indicationList = indicationsSource.map((indicationID, indicationIndex) => {
                //if no indications exist for whatever reason, exit
                if (!indicationID) return;

                //build the indication display props
                const indication = indications[indicationID];
                const definitions = indication.definitions

                const indicationName = (props.listType === 'dosing' && indication.dosingName)
                                        ? indication.dosingName
                                        : ( props.brandType === 'qlex' && indication.qlexName)
                                        ? indication.qlexName
                                        : indication.accordianStatement ?? indication.name;

                const title = (props.brandType === 'qlex' && !!indication.qlexModifier?.[0]?.modifier) 
                            ? indication.qlexModifier[0].modifier + ' ' + indicationName
                            : indication.modifier 
                            ? indication.modifier + ' ' + indicationName
                            : indicationName
                             

                const body = indication.statement;

                if (props.listType !== 'dosing' && props.listType !== 'dosingQsub') {
                    const ctaData = triageCTAs[indicationID];
                    if (!ctaData) return null; // prevent error
                    const QlexctaData = triageCTAs[indicationID];
                    if (!QlexctaData) return null;

                    
                    // There are certain cases where the Efficacy KEYNOTE numbers differ from the Safety ones
                    const keynotes = props.listType === 'efficacy'
                        ? ctaData.efficacyKeynotes || ctaData.keynotes // For Efficacy, check first for Efficacy-specific KEYNOTE numbers
                        : props.listType === 'safety' ? ctaData.safetyKeynotes || ctaData.keynotes
                        : ctaData.keynotes;

                    const cta = !ctaData.keynotes
                        ? ctaData[`${props.listType}CTA`]
                        : {
                            url: `${sharedStrings[props.listType].urlPrefix}${ctaData.slug}/`,
                            label: `View ${sharedStrings[props.listType].ctaDataPrefix} Data for KEYTRUDA From ${createKeynotesText(keynotes)}`,
                        };

                    const qlexcta = cta.QlexCTA || QlexctaData.QlexCTA;

                    const presQlex = !!cta.QlexPrescribing && cta.QlexPrescribing


                    return (
                        <IndicationCTA
                            key={indicationID}
                            title={title}
                            body={body}
                            definitions={definitions}
                            cta={cta}
                            esi={indication.esi}
                            anchorId={indication.anchorId}
                            QlexCTA={qlexcta}
                            listType={props.listType}
                            QlexPrescribing={presQlex}
                        />
                    );
                } else {
                    const footnotesData =
                                    props.listType === 'dosingQsub'
                                    ? cancerTypeFootnotesQsub
                                    : cancerTypeFootnotes;
                    return (
                        <>
                            {/* Exclude combination linkouts from Dosing Triage */}
                            {(!indication.isCombination) && <>
                                
                                <IndicationDosing
                                    title={title}
                                    // body={indicationDosingContent[indicationID]}
                                    body={
                                        props.listType === 'dosing'
                                            ? indicationDosingContent[indicationID]
                                            : indicationQlexDosingContent[indicationID]
                                    }
                                /> </>
                            }
                            {/* The last indication in each cancerType is followed by a FeaturedCTA */}
                            {(indicationIndex === cancerTypesList[cancerTypeId].indications.length - 1) &&
                                <MaxWidthComponentWrapper>
                                   <FeaturedCTA
                                        title="Review Dose Modifications for Adverse Reactions"
                                        ctas={[
                                            {
                                                label: "Dose Modifications",
                                                url: props.listType !== 'dosingQsub' && '/dosing/dose-modifications',
                                                href: props.listType === 'dosingQsub' && '/static/pdf/KEYTRUDA_QLEX_Dosing_Modification_Guide.pdf',
                                                target: props.listType === 'dosingQsub' && '_blank'
                                            },
                                        ]}
                                        iconAlt=""
                                        {...(props.listType === 'dosingQsub' ? { brandType: 'dosingQsub' } : {})}
                                    />
                                    
                                    {/* Some cancerTypes include footnotes and definitions */}
                                    

                                    {(footnotesData[cancerTypeId]?.footnotes || footnotesData[cancerTypeId]?.definitions) && (
                                        <NotationBlock
                                            footnotes={footnotesData[cancerTypeId]?.footnotes}
                                            definitions={footnotesData[cancerTypeId]?.definitions}
                                        />
                                    )}
                                    {/* {(cancerTypeFootnotes[cancerTypeId].footnotes
                                        || cancerTypeFootnotes[cancerTypeId].definitions) &&
                                        <NotationBlock
                                            footnotes={cancerTypeFootnotes[cancerTypeId].footnotes}
                                            definitions={cancerTypeFootnotes[cancerTypeId].definitions}
                                        />} */}
                                </MaxWidthComponentWrapper>}
                        </>
                    )
                }
            });

            return {
                title: cancerTypesList[cancerTypeId].modifiedTitle
                    ? cancerTypesList[cancerTypeId].modifiedTitle
                    : cancerTypesList[cancerTypeId].name,
                subtitle: cancerTypesList[cancerTypeId].modifier,
                children: props.listType === "dosing" ? indicationBrandTabwithList : indicationList,
                slug: props.listType === "dosing" && cancerTypesList[cancerTypeId]?.slug || '',
            };
        });

    if (props.homepage) {
        const defaultOpen= props.tumor!='bre';
        return (
            <>
                {filteredCancerTypes.length !== 0 && <AccordionGroup items={cancerTypeList} defaultAllOpen={defaultOpen} homepage handlTumor={props.handlTumor} tumorOpen={props.tumorOpen} />}
            </>
        );
    } else {
        return <AccordionGroup includeFilter items={cancerTypeList} defaultAllOpen={filteredCancerTypes.length} listType={props.listType} tumorID={props.tumorID} />;
    }
};

export default TriageList;